/*--------------------------------------------------------------------------------------------
Program     :  CalendarView.js
Description :  Main program for Map and Track CalendarView
Date        :  29/08/2024 
Created By  :  Fode/Jelbin
---------------------------------------------------------------------------------------------
TicketNo         Author          Date            ChangeDesc
---------------------------------------------------------------------------------------------
29082024        - Fode/Jelbin     -  29/08/2024        Created
---------------------------------------------------------------------------------------------*/
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useMapTrackInfo } from "../context/Maptrackcontext";
import TimeCalculationService from '../service/TimeCalculationService';
import CircularProgress from '@mui/material/CircularProgress';
import MainService from "../service/MainService";
import TaskDialog from './TaskDialog';
import PoiwarningDialog from "./PoiwarningDialog";
import HolidayDialog from './HolidayDialog';
import HolidayWrngDialog from './HolidayWrngDialog';
import PriorityWebSDKService from "../service/PriorityWebSDKService";

const styles = {
  calendarContainer: {
    width: '100%',
    height: '100%',
    padding: '16px',
    overflow: 'hidden',
  },
  calendar: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: '16px',
    borderBottom: '1px solid #e0e0e0',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: '0',
  },
  gridContainer: {
    flexGrow: 1,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  gridContent: {
    overflow: 'auto',
    height: '100%',
    position: 'relative',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
    },
  },
  headerGrid: {
    display: 'grid',
    gridTemplateColumns: (dayCount) => `200px repeat(${dayCount}, 250px)`,
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: 'white',
    width: 'max-content'
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: (dayCount) => `200px repeat(${dayCount}, 250px)`,
    position: 'relative',
    minWidth: 'max-content'
  },
  headerCell: {
    padding: '12px',
    textAlign: 'center',
    borderRight: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f8fafc',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fixedCorner: {
    position: 'sticky',
    left: 0,
    backgroundColor: '#f8fafc',
    borderRight: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
    zIndex: 3,
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
  },
  userCell: {
    padding: '12px',
    borderRight: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f8fafc',
    position: 'sticky',
    left: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    width: '200px',
  },
  dayCell: {
    padding: '8px',
    borderRight: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
    minHeight: '120px',
    backgroundColor: 'white',
  },
  taskList: {
    minHeight: '100%'
  },
  taskItem: {
    padding: '8px',
    marginBottom: '4px',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    cursor: 'move',
  },
  taskItemDragging: {
    backgroundColor: '#f0f9ff',
    borderColor: '#93c5fd',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  taskTime: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#4b5563',
    marginRight: '8px',
  },
  taskContent: {
    flex: '1',
    fontSize: '14px',
    fontWeight: '500',
  }
};

const CalendarView = ({ dateRange, onTaskMove, selectedtraveler, ZDATERANGE, messageFields,
  popupFields, tableUniqueName, fieldPOIsave, durationFields, fielduserstartTime, tskTravelTimeField,
  finalTravelTimeData, mapVisibilityDate, startTimeField, endTimeField, fixschedulefield,
  subformName, mainFormName, editablefield, participantsData, mergeparticipantFields,
  participantsFields, statuscolorField, selectedSourceMapname,
  // paramater <TaskDialog.js>
  startsave,
  messageData,
  reassignMapData,
  travelerName,
  selectedUKey,
  POIExist,
  selectedChooseData,
  deletePOI,
  note,
  noteData,
  editNote,
  description,
  noteLocalSave,
  tableUniqueValue,
  chooseOptions,
  dataModified,
  poiData,
  handleDlgClose,
  handleUserChange,
  handlePOIUKey,
  handledeletePOI,
  handleDataReassign,
  handleChooseData,
  handleNote,
  handleNoteEdit,
  handleNoteView,
  setEditorState,
  imageUploadCallback,
  selectedagentindexval,
  setChooseOptions,
  envFileData }) => {
   console.log("dateRangeRcvd",dateRange);
  const days = useMemo(() => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const dayCount = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const days = [];
    for (let i = 0; i < dayCount; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date.toLocaleDateString('en-GB', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }));
    }
    return days;
  }, [dateRange]);


  const { combinedData, setCombinedData, timeATPOI, dlgopen, setdlgOpen, setMessageData, setnoteData,
    setSelectedChooseData, setravelerName, setPOIExist, setReassignMapData, setSourceIndexVal,
    setoldSourceindex, settableUniqueValue, setDataModified, selectedCalenderTaskVal,
    setselectedCalenderTaskVal, participantTaskTime, setParticipantTaskTime,
    selectedCalenderTaskDate, setselectedCalederTaskDate, holidayPayload,
    setFinalTravelTimeData, setTravelbuttonEnable, setSavebuttonEnable,agentColor, setagentColor } = useMapTrackInfo();
  const [isProcessing, setIsProcessing] = useState(false);
  const [calendertaskClicked, setcalendertaskClicked] = useState(false);
  const [calenderheaderIndexval, setCalenderHeaderIndexVal] = useState(0);
  const [tasks, setTasks] = useState({});
  const [dragResultClndr, setDragResultClndr] = useState({});
  const [showPOIDialog, setShowPOIDialog] = useState(false);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [showHolidayWrngDialog, setShowHolidayWrngDialog] = useState(false);

  // This useEffect using to convert the combinedData to calender format.
  // The data display in calendar from setTasks hook
  useEffect(() => {
    const taskMap = {};

    combinedData.forEach(user => {
      const userLogin = user[selectedtraveler];

      user.details?.forEach((task, taskIndex) => {
        // Only process tasks that belong to this user
        if (task[selectedtraveler] === userLogin) {
          const taskDate = new Date(task[ZDATERANGE].slice(0, 10)); //Added .slice when the issue comes during time zone change
          const dateStr = taskDate.toLocaleDateString('en-GB', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          });

          const cellId = `${userLogin}-${dateStr}`;

          if (!taskMap[cellId]) {
            taskMap[cellId] = [];
          }

          taskMap[cellId].push({
            id: `task-${task[tableUniqueName]}`,
            userID: userLogin,
            taskIndxVal: taskIndex,
            taskDateVal: task[ZDATERANGE],
            content: popupFields
              .filter(field => field.POPUP === 'Y')
              .map(field => task[field.NAME])
              .join(' - '),
            location: task[fieldPOIsave],
            taskcolor: task[statuscolorField],
            userColor:task.color
          });
        }
      });
    });

    setTasks(taskMap);
  }, [combinedData]);


  // function to convert date from this "Mon, 30 Jun"  to 2025-06-30
  const formatDateToISO = (inputDate) => {
    const currentYear = new Date().getFullYear();
    const fullDateStr = `${inputDate} ${currentYear}`; // e.g., "Mon, 30 Jun 2025"
    const dateObj = new Date(fullDateStr);

    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
  }


  const LoadingOverlay = () => (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      cursor: 'not-allowed'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <CircularProgress size={40} />
        <div>Processing task reassignment...</div>
      </div>
    </div>
  );


  const handleTaskClick = (task) => {
    setcalendertaskClicked(true);
    setDataModified(false);
    setdlgOpen(true);
    const ztableUnique = task.id.split("-")[1];
    const ztaskUderId = task.userID;
    const filteredDetails = combinedData.flatMap(user => user.details).filter(detail => detail[tableUniqueName] === parseInt(ztableUnique));
    const zheaderindex = combinedData.findIndex(user => user[selectedtraveler] === ztaskUderId);
    setselectedCalenderTaskVal(filteredDetails[0]);
    setselectedCalederTaskDate(filteredDetails[0][ZDATERANGE].split('T')[0]);
    setCalenderHeaderIndexVal(zheaderindex);
    setagentColor(task.userColor)
    // Get Message Details
    const filteredA1 = {};
    messageFields.forEach((key) => {
      if (key.NAME in filteredDetails[0]) {
        filteredA1[key.TITLE] = filteredDetails[0][key.NAME];
      }
    });
    setMessageData(filteredA1);
    // Get editablevalue
    const editablevalue = {};
    editablefield.forEach((key) => {
      if (key.NAME in filteredDetails[0]) {
        editablevalue[key.NAME] = filteredDetails[0][key.NAME];
      }
    });

    setSelectedChooseData(editablevalue);
    setravelerName(filteredDetails[0][selectedtraveler]);
    setReassignMapData(filteredDetails[0]);
    setSourceIndexVal(task.taskIndxVal);
    setoldSourceindex(task.taskIndxVal);
    settableUniqueValue(parseInt(ztableUnique.trim()));

    // Calling service to get the Note data for on the selected Task
    if (subformName) {
      MainService.getNoteData(mainFormName, subformName, ztableUnique)
        .then((noteresponse) => {
          setnoteData(noteresponse.data.TEXT);
        })
        .catch((err) => console.error("Error fetching POI data:", err));
    }

    if (filteredDetails[0].pointsOfInterest !== undefined) {
      setPOIExist(true);
    }
  }

  const handlePOIDlgYes = () => {
    setShowPOIDialog(false);
    handleProcesshandleDragEnd(dragResultClndr);
    setDragResultClndr({});
  };

  const handlePOIDlgNo = () => {
    setShowPOIDialog(false);
    setDragResultClndr({});
  };

  const handleHolidayYes = () => {
    setShowHolidayDialog(false);
    setdlgOpen(false);
    handleProcesshandleDragEnd(dragResultClndr); // comment if block the assgnemt when user holiday
    setDragResultClndr({});
  }

  const handleHolidayWrngYes = () => {
    setShowHolidayWrngDialog(false);
  }

  const handleDragEnd = async (result) => {

    const desttaskDate = formatDateToISO(result.destination.droppableId.split("-")[1]);
    const desttaskUser = result.destination.droppableId.split("-")[0];

    // Check is the destination user is on Holiday or not
    const isUserHoliday = TimeCalculationService.handlecheckisUserHoliday(holidayPayload, selectedtraveler, desttaskUser, desttaskDate);
    if (isUserHoliday) {
      setShowHolidayDialog(true);
      setDragResultClndr(result);
    }
    else {
      const ztableUnique = result.draggableId.split("-")[1];
      const zselectedtask = combinedData.flatMap(user => user.details).filter(detail => detail[tableUniqueName] === parseInt(ztableUnique));
      if (zselectedtask[0].pointsOfInterest !== undefined) {
        setDragResultClndr(result);
        setShowPOIDialog(true);
      }
      else {
        handleProcesshandleDragEnd(result);
      }
    }
  }

  
  const handleProcesshandleDragEnd = async (result) => {
    setIsProcessing(true);
    setDataModified(true);
    setTravelbuttonEnable(true);
    setSavebuttonEnable(false);
  
    const { source, destination, draggableId } = result;
  
    if (
      !destination ||
      (source.droppableId === destination.droppableId && source.index === destination.index)
    ) {
      setIsProcessing(false);
      setDataModified(false);
      return;
    }
  
    // helper: parse droppableId "user-Month,Day" or "user-Month,Day-header"
    const parseDroppable = (droppableId) => {
      const parts = droppableId.split("-");
      const user = parts[0];
      const datePart = parts[1] || "";
      return { user, datePart };
    };
  
    // helper: format date string from droppable date part and year
    // const formatDateFromPart = (datePart, year) => {
    //   console.log("datePart, year",datePart, year);
    //   if (!datePart) return null;
    //   const [monthStr, dayStr] = datePart.split(",");
    //   const fullDateStr = `${monthStr} ${dayStr}, ${year}`;
    //   const d = new Date(fullDateStr);
    //   const y = d.getFullYear();
    //   const m = String(d.getMonth() + 1).padStart(2, "0");
    //   const dd = String(d.getDate()).padStart(2, "0");
    //   // keep your +01:00 offset as before (adjust if you need UTC)
    //   return `${y}-${m}-${dd}T00:00:00+01:00`;
    // };

    const formatDateFromPart = (datePart, year) => {
      if (!datePart) return null;
    
      const parts = datePart.split(" "); // ["Thu", "18", "Dec"]
      const day = parts[1];
      const month = parts[2];
    
      const fullDateStr = `${month} ${day}, ${year}`;
      const d = new Date(fullDateStr);
    
      if (isNaN(d)) return null;
    
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
    
      return `${y}-${m}-${dd}T00:00:00+01:00`;
    };
    
    // strip prefix 'task-' from draggable id
    const taskUniqueId = draggableId.replace(/^task-/, "");
  
    // keep a local reference to year
    const yearFromRange = new Date(dateRange.start).getFullYear();
    const yearToRange = new Date(dateRange.end).getFullYear();
    console.log("yearFromRange",yearFromRange,dateRange.start);
    
    try {
      // Update the tasks UI structure (you already had this)
      setTasks((prevTasks) => {
        const newTasks = { ...prevTasks };
        const sourceList = Array.from(prevTasks[source.droppableId] || []);
        const destList =
          source.droppableId === destination.droppableId
            ? sourceList
            : Array.from(prevTasks[destination.droppableId] || []);
  
        const [removed] = sourceList.splice(source.index, 1);
  
        if (source.droppableId === destination.droppableId) {
          sourceList.splice(destination.index, 0, removed);
          newTasks[source.droppableId] = sourceList;
        } else {
          destList.splice(destination.index, 0, removed);
          newTasks[source.droppableId] = sourceList;
          newTasks[destination.droppableId] = destList;
        }
  
        Object.keys(newTasks).forEach((key) => {
          if (!newTasks[key] || newTasks[key].length === 0) {
            delete newTasks[key];
          }
        });
        return newTasks;
      });

      // update combinedData atomically using previous state (avoids stale reads)
      let updatedDataFinal = null;
      await new Promise((resolve) => {
        setCombinedData((prevCombined) => {
          // deep clone prevCombined so we don't mutate React state
          const updated = JSON.parse(JSON.stringify(prevCombined));
  
          // find agent indices by selectedtraveler value (same logic as before)
          const sourceAgentIndex = updated.findIndex(
            (agent) => agent[selectedtraveler] === parseDroppable(source.droppableId).user
          );
          const destAgentIndex = updated.findIndex(
            (agent) => agent[selectedtraveler] === parseDroppable(destination.droppableId).user
          );
  
          // If your variable name is `selectedtraveler` (typo in code above),
          // ensure the correct var is used. I use `selectedtraveler` consistently here.
          // Re-find properly if your real env uses different name:
          // const destAgentIndex = updated.findIndex(agent => agent[selectedtraveler] === parseDroppable(destination.droppableId).user);
  
          if (sourceAgentIndex === -1 || destAgentIndex === -1) {
            // keep previous state (no change) and resolve
            updatedDataFinal = prevCombined;
            resolve();
            return prevCombined;
          }

          // Find source task index inside agent.details using tableUniqueName
          const sourceTaskIndex = updated[sourceAgentIndex].details.findIndex(
            (t) => String(t[tableUniqueName]) === String(taskUniqueId));
  
          if (sourceTaskIndex === -1) {
            // task not found: no-op
            updatedDataFinal = prevCombined;
            resolve();
            return prevCombined;
          }
  
          // compute formatted dates
          const formattedDateSource = formatDateFromPart(parseDroppable(source.droppableId).datePart, yearFromRange);
          const formattedDateDest = formatDateFromPart(parseDroppable(destination.droppableId).datePart, yearToRange);
          console.log("date:",formattedDateSource,formattedDateDest);
          // remove moved item
          const [movedItem] = updated[sourceAgentIndex].details.splice(sourceTaskIndex, 1);
  
          // prepare updated moved item
          const updatedMovedItem = {
            ...movedItem,
            participantFinalTrvltime: [], // clear participantFinalTrvltime block
            color: updated[destAgentIndex].color,
            [selectedtraveler]: updated[destAgentIndex][selectedtraveler],
            [ZDATERANGE]: formattedDateDest,
            recordchange: true,
          };

          // Determine destination insert index (destTaskIndex) robustly:
          // We want to insert among tasks for the same date group so order within date is preserved.
          // Compute tasks on that date after removal.
          const tasksWithIndex = updated[destAgentIndex].details.map((task, idx) => ({ task, idx }));
  
          const formatToDay = (iso) => (iso ? iso.split("T")[0] : null);
          const targetDay = formatToDay(formattedDateDest);
  
          const tasksOnSameDay = tasksWithIndex.filter(
            (it) => formatToDay(it.task[ZDATERANGE]) === targetDay
          );
  
          let destTaskIndex;
          if (tasksOnSameDay.length === 0) {
            // no tasks on that date: find first task with date > target or append
            destTaskIndex = updated[destAgentIndex].details.findIndex(
              (t) => new Date(t[ZDATERANGE]) > new Date(formattedDateDest)
            );
            if (destTaskIndex === -1) destTaskIndex = updated[destAgentIndex].details.length;
          } else {
            // There are tasks on that date: we need to place at logical position using destination.index
            // destination.index is the index within the droppable (date group) as shown in UI.
            if (destination.index <= 0) {
              destTaskIndex = tasksOnSameDay[0].idx;
            } else if (destination.index >= tasksOnSameDay.length) {
              destTaskIndex = tasksOnSameDay[tasksOnSameDay.length - 1].idx + 1;
            } else {
              destTaskIndex = tasksOnSameDay[destination.index].idx;
            }
          }

          // If moved within the same agent and the removal index was before the insert point,
          // adjust insert index because we've already removed one element from source list.
          if (sourceAgentIndex === destAgentIndex) {
            // If source removal was earlier in the array than the computed dest index, decrement dest index by 1
            // because the array is now shorter after removal.
            const rawSourceIndexInDetails = sourceTaskIndex;
            if (rawSourceIndexInDetails < destTaskIndex) {
              destTaskIndex -= 1;
              if (destTaskIndex < 0) destTaskIndex = 0;
            }
          }
  
          // Insert
          updated[destAgentIndex].details.splice(destTaskIndex, 0, updatedMovedItem);

          // Add participants if agent changed or date changed
          const sameAgent = sourceAgentIndex === destAgentIndex;
          const sameDate = formattedDateSource && formattedDateDest && formattedDateSource.split("T")[0] === formattedDateDest.split("T")[0];
  
          if (!sameAgent || !sameDate) {
            const ZupdatedetailParticipants = TimeCalculationService.handleAddparticipants(
              updated,
              destAgentIndex,
              participantsFields,
              movedItem[tableUniqueName],
              tableUniqueName
            );
            updated[destAgentIndex].details = ZupdatedetailParticipants;
          }
          
          // set expanded on dest agent
          updated[destAgentIndex].expanded = true;
  
          // set totalTime to 0 for agents with no details
          const final = updated.map((ag) => (ag.details.length === 0 ? { ...ag, totalTime: 0 } : ag));
  
          updatedDataFinal = final;
          resolve();
          return final;
        }); // end setCombinedData functional updater
      }); // end new Promise
  
      // now updatedDataFinal contains the latest updated combined data
      if (!updatedDataFinal) {
        // safety fallback: read from combinedData prop/state
        updatedDataFinal = combinedData;
      }
      // After drag and drop ,Show  the warning message if any of the participant is on absent 
      // for the date under the destiantion user.
      const desttaskDateISO = formatDateFromPart(parseDroppable(destination.droppableId).datePart, yearToRange);
      const desttaskUser = parseDroppable(destination.droppableId).user;
      for (const agentblock of updatedDataFinal) {
        if (agentblock[selectedtraveler] === desttaskUser) {
          for (const participantblock of agentblock.ZCLA_TEAMMEMBERSR_SUBFORM) {
            const isUserHoliday = TimeCalculationService.handlecheckisUserHoliday(
              holidayPayload,
              selectedtraveler,
              participantblock[selectedtraveler],
              desttaskDateISO.slice(0, 10)
            );
            if (isUserHoliday) {
              setShowHolidayWrngDialog(true);
              break;
            }
          }
          break;
        }
      }
      // propagate move event if any callback
      if (onTaskMove) {
        onTaskMove(result, updatedDataFinal);
      }
    } catch (err) {
      console.error("Error moving task:", err);
    } finally {
      setIsProcessing(false);
    }
  };
  

  return (

    <div style={styles.calendarContainer}>
      {/* {taskClicked && TaskPopupComponent && <TaskPopupComponent />} */}
      <div style={styles.calendar}>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>Team Calendar</h2>
        </div>

        <div style={styles.gridContainer}>
          {isProcessing && <LoadingOverlay />}
          <div style={styles.gridContent}>
            {/* Header row with fixed corner */}
            <div style={{ ...styles.headerGrid, gridTemplateColumns: styles.headerGrid.gridTemplateColumns(days.length) }}>
              <div style={styles.fixedCorner}>Installer</div>
              {days.map(day => (
                <div key={day} style={styles.headerCell}>{day}</div>
              ))}
            </div>
            {/* Main grid with tasks */}
            <DragDropContext onDragEnd={handleDragEnd} >
              <div style={{ ...styles.mainGrid, gridTemplateColumns: styles.mainGrid.gridTemplateColumns(days.length) }}>
                {combinedData.map(user => (
                  <React.Fragment key={user[selectedtraveler]}>
                    <div style={styles.userCell}>{user[selectedtraveler]}</div>
                    {days.map(day => {
                      const Userid = `${user[selectedtraveler]}-${day}`;
                      // Check the logic for user holiday 
                      const isUserHoliday = TimeCalculationService.handlecheckisUserHoliday(holidayPayload, selectedtraveler,
                        user[selectedtraveler], formatDateToISO(day));
                      return (
                        <div key={Userid} style={styles.dayCell} >
                          <Droppable droppableId={Userid}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{ ...styles.taskList, backgroundColor: isUserHoliday ? '#E1BEE7' : undefined }}
                              >
                                {isUserHoliday ?
                                  <span style={{ color: 'red', fontWeight: 'bold', padding: '2px 6px', fontSize: '15px' }}>Holiday</span> : <></>}
                                {(tasks[Userid] || []).map((task, index) => (
                                  <Draggable
                                    key={task.id}
                                    draggableId={task.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                          ...styles.taskItem,
                                          ...(snapshot.isDragging ? styles.taskItemDragging : {}),
                                          ...provided.draggableProps.style,
                                          backgroundColor: task.taskcolor,  // task color
                                        }}
                                        onClick={() => handleTaskClick(task)}
                                      >
                                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }} >
                                          <span style={styles.taskContent}>{task.content}</span> {/*Display task*/}
                                          {task.location && <span style={{ fontSize: '12px', color: '#6b7280' }}> 📍(POI) {task.location}</span>}
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                          {/* New div for participant task time */}
                          {/* <div style={{ borderTop: '1px solid rgb(235, 233, 229)', margin: '2px 0' }}></div> */}
                          <div
                            style={{
                              marginTop: '-25px',
                              fontSize: '12px',
                              color: 'rgb(107, 114, 128)',
                              textAlign: 'center'
                            }}
                          >
                            {participantTaskTime.map((tastimevalue, index) => {
                              if (
                                tastimevalue.USERLOGIN === user[selectedtraveler] &&
                                tastimevalue.DATE.startsWith(formatDateToISO(day))
                              ) {
                                return (
                                  <div key={index}>
                                    Participant in {tastimevalue.PARTCIPANTCOUNT} task for: {tastimevalue.PARTITASKTIME} Hours
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </DragDropContext>

          </div>
        </div>
      </div>
      {calendertaskClicked && selectedCalenderTaskVal && (
        <>
          <PriorityWebSDKService setChooseOptions={setChooseOptions}
            editablefield={editablefield} selectedSourceMain={mainFormName}
            envFileData={envFileData} tableUniqueName={tableUniqueName}
            tableUniqueValue={tableUniqueValue} />
          <TaskDialog
            dlgopen={dlgopen}
            startsave={startsave}
            messageData={messageData}
            reassignMapData={reassignMapData}
            travelerName={travelerName}
            selectedUKey={selectedUKey}
            POIExist={POIExist}
            selectedChooseData={selectedChooseData}
            deletePOI={deletePOI}
            note={note}
            noteData={noteData}
            editNote={editNote}
            description={description}
            noteLocalSave={noteLocalSave}
            tableUniqueValue={tableUniqueValue}
            agentColor={agentColor}
            combinedData={combinedData}
            selectedtraveler={selectedtraveler}
            poiData={poiData}
            editablefield={editablefield}
            chooseOptions={chooseOptions}
            subformName={subformName}
            dataModified={dataModified}
            handleDlgClose={handleDlgClose}
            handleUserChange={handleUserChange}
            handlePOIUKey={handlePOIUKey}
            handledeletePOI={handledeletePOI}
            handleDataReassign={handleDataReassign}
            handleChooseData={handleChooseData}
            handleNote={handleNote}
            handleNoteEdit={handleNoteEdit}
            handleNoteView={handleNoteView}
            setEditorState={setEditorState}
            imageUploadCallback={imageUploadCallback}
            //input for ParticipantsDialog.js
            selectedtask={selectedCalenderTaskVal}
            selectedagentindexval={calenderheaderIndexval}
            participantsData={participantsData}
            tableUniqueName={tableUniqueName}
            tskTravelTimeField={tskTravelTimeField}
            mapVisibilityDate={selectedCalenderTaskDate}
            ZDATERANGE={ZDATERANGE}
            durationFields={durationFields}
            fielduserstartTime={fielduserstartTime}
            startTimeField={startTimeField}
            endTimeField={endTimeField}
            fixschedulefield={fixschedulefield}
            selectedSourceMapname={selectedSourceMapname}
          />
        </>
      )}
      {showPOIDialog && (
        <PoiwarningDialog
          dlgPoiWarning={showPOIDialog}
          handlePOIDlgYes={handlePOIDlgYes}
          handlePOIDlgNo={handlePOIDlgNo}
        />
      )}
      {showHolidayDialog && <HolidayDialog
        dlgHoliday={showHolidayDialog}
        handleHolidayYes={handleHolidayYes} />}

      {showHolidayWrngDialog && <HolidayWrngDialog
        dlgHolidayWrng={showHolidayWrngDialog}
        handleHolidayWrngYes={handleHolidayWrngYes} />}
    </div>

  );
};

export default CalendarView;